import { motion } from 'framer-motion';
import { useState } from 'react';
import { ArrowRight, CheckCircle, School } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { API } from '@/lib/api';

const SCHOOL_PORTAL_URL = 'https://school.vibhumedusys.com/';

const initialFormData = {
  name: '',
  email: '',
  password: '',
  confirm_password: '',
  phone_number: '',
  school_name: '',
  school_address: '',
  school_pincode: '',
};

export default function RegisterPage() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    const { confirm_password, ...payload } = formData;

    try {
      await axios.post(`${API}/register`, payload);
      toast.success('Registration successful!');
      setIsSuccess(true);
      setFormData(initialFormData);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (typeof detail === 'object' && detail !== null) {
        const firstError = Object.values(detail).flat()[0];
        toast.error(firstError || 'Registration failed. Please try again.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      console.error('Error submitting registration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="register-page" className="pt-20">
      <Toaster position="top-center" />
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-block bg-emerald-light/10 border border-emerald/20 px-4 py-2 rounded-full mb-6">
              <span className="text-emerald font-medium text-sm">
                Free Trial - No Credit Card Required
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black text-navy mb-6 font-heading">
              Start Your Free Trial
            </h1>
            <p className="text-base leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Create your school account in minutes and get instant access to
              the VibhumEduSys portal.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 lg:p-10 rounded-2xl border border-slate-200 shadow-lg"
            >
              {/* <h2 className="text-2xl font-bold text-navy mb-6 font-heading">
                Create Your Account
              </h2> */}
              {isSuccess ? (
                <div
                  data-testid="success-message"
                  className="text-center py-12"
                >
                  <CheckCircle className="h-16 w-16 text-emerald mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-navy mb-2">
                    You're All Set!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Your school account has been created. Log in to the portal
                    with your email and password to get started.
                  </p>
                  <a href={SCHOOL_PORTAL_URL}>
                    <Button
                      data-testid="portal-login-button"
                      className="bg-emerald hover:bg-emerald-light text-white font-semibold"
                    >
                      Go to Portal
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="register-form">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Your Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="register-name-input"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="e.g. Rajesh Kumar"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        data-testid="register-email-input"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="password">Password *</Label>
                        <Input
                          id="password"
                          name="password"
                          data-testid="register-password-input"
                          type="password"
                          required
                          minLength={6}
                          value={formData.password}
                          onChange={handleChange}
                          className="mt-1"
                          placeholder="Min. 6 characters"
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm_password">
                          Confirm Password *
                        </Label>
                        <Input
                          id="confirm_password"
                          name="confirm_password"
                          data-testid="register-confirm-password-input"
                          type="password"
                          required
                          minLength={6}
                          value={formData.confirm_password}
                          onChange={handleChange}
                          className="mt-1"
                          placeholder="Re-enter password"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone_number">Phone Number *</Label>
                      <Input
                        id="phone_number"
                        name="phone_number"
                        data-testid="register-phone-input"
                        type="tel"
                        required
                        value={formData.phone_number}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="school_name">School / Institute Name *</Label>
                      <Input
                        id="school_name"
                        name="school_name"
                        data-testid="register-school-input"
                        type="text"
                        required
                        value={formData.school_name}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="e.g. Delhi Public School"
                      />
                    </div>

                    <div>
                      <Label htmlFor="school_address">School Address *</Label>
                      <Input
                        id="school_address"
                        name="school_address"
                        data-testid="register-address-input"
                        type="text"
                        required
                        value={formData.school_address}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="123 Main Street, City"
                      />
                    </div>

                    <div>
                      <Label htmlFor="school_pincode">Pincode *</Label>
                      <Input
                        id="school_pincode"
                        name="school_pincode"
                        data-testid="register-pincode-input"
                        type="text"
                        required
                        pattern="[0-9]{6}"
                        value={formData.school_pincode}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="110001"
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="register-submit-button"
                      disabled={isSubmitting}
                      className="w-full bg-emerald hover:bg-emerald-light text-white font-semibold py-6 text-lg"
                    >
                      {isSubmitting ? 'Creating Account...' : 'Create Account'}
                      {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>

                    <p className="text-xs text-slate-500 text-center">
                      By creating an account, you agree to our Terms of Service
                      and Privacy Policy.
                    </p>
                  </div>
                </form>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col justify-center"
            >
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-navy mb-4 font-heading">
                  What You Get Instantly
                </h3>
                <div className="space-y-4">
                  {[
                    'Full access to the school management portal',
                    'Student and staff management tools',
                    'Fee collection and attendance tracking',
                    'Parent communication features',
                    'Dedicated support to help you get started',
                  ].map((item, index) => (
                    <div
                      key={index}
                      data-testid={`register-benefit-${index}`}
                      className="flex items-center space-x-3"
                    >
                      <CheckCircle className="h-6 w-6 text-emerald flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-emerald-light/10 border-2 border-emerald/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <School className="h-6 w-6 text-emerald" />
                  <h4 className="font-semibold text-navy">Already registered?</h4>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Log in to your existing school portal to manage operations.
                </p>
                <a href={SCHOOL_PORTAL_URL}>
                  <Button
                    variant="outline"
                    data-testid="existing-portal-button"
                    className="border-2 border-emerald text-emerald hover:bg-emerald/5 font-semibold"
                  >
                    Go to Portal
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
